import { CompressedSplatData } from '@/plugins/gsplat/core/CompressedSplatData'
import {
  GaussianSplatMesh,
  type GaussianSplatMeshOptions,
} from '@/plugins/gsplat/core/GaussianSplatMesh'
import { type PlyElement, type PlyProperty, SplatData } from '@/plugins/gsplat/core/SplatData'
import type { ErrorCallback, SimpleProgressCallback } from '@/plugins/gsplat/types/EventTypes'
import { CoordinateSystem, CoordinateSystemType } from '@/plugins/gsplat/utils/CoordinateSystem'
import { SplatError, SplatErrorCode } from '@/plugins/gsplat/utils/SplatError'
import { SplatValidator } from '@/plugins/gsplat/utils/SplatValidator'
import { getLoggerManager } from '@/shared/logging'
import { parseSplat } from './SplatParser'
import { StreamBuf } from './StreamBuf'

const log = getLoggerManager().getLogger('gsplat:loader')

/**
 * PLY Header解析结果
 */
interface PlyHeader {
  elements: PlyElement[]
  headerEnd: number // header 结束位置（binary body 开始位置）
  isCompressed: boolean // 是否为压缩格式
}

/**
 * Splat 加载器选项
 */
export interface SplatLoaderOptions {
  /** 调试模式：启用数据验证和详细日志 */
  debugMode?: boolean

  /** 预处理选项：过滤低透明度splat的阈值（0-1），默认不过滤 */
  alphaRemovalThreshold?: number

  /** 预处理选项：归一化四元数，默认true */
  normalizeQuaternions?: boolean

  /** 预处理选项：修正负缩放，默认true */
  fixNegativeScales?: boolean

  /** 坐标系：源坐标系类型，默认自动检测 */
  coordinateSystem?: CoordinateSystemType

  /** 坐标系：目标坐标系类型，默认Y_UP */
  targetCoordinateSystem?: CoordinateSystemType

  /** 进度回调：报告加载进度 (progress: 0-1, stage: 当前阶段描述) */
  onProgress?: SimpleProgressCallback

  /** 错误回调：报告加载错误 */
  onError?: ErrorCallback

  /** 是否保留压缩格式（默认 false，自动解压以兼容现有代码；设为 true 可节省 3-7x 内存） */
  keepCompressed?: boolean

  /** 属性过滤器：只加载指定的属性（如 ['x', 'y', 'z', 'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity', 'scale_0', 'scale_1', 'scale_2', 'rot_0', 'rot_1', 'rot_2', 'rot_3']） */
  propertyFilter?: string[]

  /** 跳过 SH 数据（等效于过滤掉 f_rest_* 属性） */
  skipSH?: boolean

  /** 中止信号：用于取消加载 */
  signal?: AbortSignal

  /** 流式加载：使用流式解析降低内存峰值（默认 false，使用一次性加载） */
  streaming?: boolean
}

/**
 * 3DGS PLY文件加载器
 *
 * 支持标准的 binary_little_endian 格式
 * 典型PLY结构：
 * ply
 * format binary_little_endian 1.0
 * element vertex N
 * property float x
 * property float y
 * ...
 * end_header
 * [binary data]
 */
export class SplatLoader {
  /**
   * 从 URL 加载 PLY 文件（带预处理）。
   *
   * @param url - PLY 资源 URL
   * @param options - 加载选项，支持 streaming、signal、keepCompressed 等
   * @returns 解析并预处理后的 SplatData
   */
  async load(url: string, options?: SplatLoaderOptions): Promise<SplatData> {
    const opts = { ...options }

    // 检查中止信号
    if (opts.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    // 根据配置选择加载方式
    if (opts.streaming) {
      // 使用流式加载
      return this.loadStreaming(url, opts)
    }

    // 1. 一次性加载文件
    opts.onProgress?.(0, '加载文件')
    const buffer = await this.fetchWithProgress(url, opts)

    // 检查中止信号
    if (opts.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    // 2. 解析（使用高级API支持压缩格式选项）
    opts.onProgress?.(0.3, '解析数据')
    const data = opts.keepCompressed
      ? this.parseAdvanced(buffer, {
          keepCompressed: true,
          ...(opts.propertyFilter ? { propertyFilter: opts.propertyFilter } : {}),
          ...(opts.skipSH !== undefined ? { skipSH: opts.skipSH } : {}),
        })
      : this.parse(buffer, opts)

    // 3. 如果是压缩格式，直接返回（跳过验证和预处理）
    // 注意：由于类型签名返回 SplatData，这里必须是解压格式
    if (!(data instanceof SplatData)) {
      throw new Error('压缩格式不支持预处理，请使用 parseAdvanced')
    }

    // 检查中止信号
    if (opts.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    // 4. 验证（如果启用debugMode）
    if (opts.debugMode) {
      opts.onProgress?.(0.6, '验证数据')
      const result = SplatValidator.validateSplatData(data)
      result.warnings.forEach((w) => log.warn(`[ThreeGS] ${w}`))
      if (!result.valid) {
        throw new SplatError(SplatErrorCode.INVALID_BINARY_DATA, result.errors.join('\n'))
      }
    }

    // 5. 预处理
    opts.onProgress?.(0.7, '预处理')
    this.preprocess(data, opts)

    opts.onProgress?.(1.0, '完成')
    return data
  }

  /**
   * 使用流式解析加载PLY文件（降低内存峰值）
   * @private
   */
  private async loadStreaming(url: string, options?: SplatLoaderOptions): Promise<SplatData> {
    const opts = { ...options }

    // 1. 发起请求
    opts.onProgress?.(0, '连接服务器')
    const response = await fetch(url, opts.signal ? { signal: opts.signal } : undefined)

    if (!response.ok) {
      throw new SplatError(
        SplatErrorCode.INVALID_BINARY_DATA,
        `加载失败: ${response.status} ${response.statusText}`
      )
    }

    if (!response.body) {
      throw new Error('Response body is null')
    }

    // 2. 流式解析
    const totalLength = parseInt(response.headers.get('content-length') ?? '0', 10)
    let totalReceived = 0

    const progressFunc = (bytes: number) => {
      totalReceived += bytes
      if (totalLength > 0) {
        opts.onProgress?.((totalReceived / totalLength) * 0.8, '流式解析')
      }
    }

    const reader = response.body.getReader()
    const data = await this.readPlyStreaming(reader, opts, progressFunc)

    // 3. 验证（如果启用debugMode）
    if (opts.debugMode) {
      opts.onProgress?.(0.85, '验证数据')
      const result = SplatValidator.validateSplatData(data)
      result.warnings.forEach((w) => log.warn(`[ThreeGS] ${w}`))
      if (!result.valid) {
        throw new SplatError(SplatErrorCode.INVALID_BINARY_DATA, result.errors.join('\n'))
      }
    }

    // 4. 预处理
    opts.onProgress?.(0.9, '预处理')
    this.preprocess(data, opts)

    opts.onProgress?.(1.0, '完成')
    return data
  }

  /**
   * 从URL加载PLY文件并直接返回 GaussianSplatMesh
   * @param url PLY 文件 URL
   * @param options 加载配置选项
   * @param meshOptions 渲染配置选项
   * @returns GaussianSplatMesh 实例
   */
  async loadMesh(
    url: string,
    options?: SplatLoaderOptions,
    meshOptions?: GaussianSplatMeshOptions
  ): Promise<GaussianSplatMesh> {
    const splatData = await this.load(url, options)
    return new GaussianSplatMesh(splatData, meshOptions)
  }

  /**
   * 解析 PLY 文件的 ArrayBuffer，默认返回 SplatData（解压格式）。
   *
   * @param buffer - 文件原始字节
   * @param options - 可选解析参数，如 propertyFilter
   * @returns 解析后的 SplatData
   */
  parse(buffer: ArrayBuffer, options?: SplatLoaderOptions): SplatData {
    // 1. 检测文件格式
    const format = this.detectFormat(buffer)

    // 2. 根据格式选择解析器
    if (format === 'splat') {
      return this.parseSplatFormat(buffer)
    }

    // 3. 解析 PLY header
    const header = this.parseHeader(buffer)

    // 4. 根据 PLY 格式类型选择解析方法
    if (header.isCompressed) {
      const compressed = this.parseCompressedBody(buffer, header)

      // 只有明确指定 keepCompressed: true 时才保留压缩格式
      // 但由于返回类型是 SplatData，这里始终解压
      return this.decompressToSplatData(compressed)
    }

    // 5. 解析标准 binary body
    this.parseBody(buffer, header.headerEnd, header.elements, options)

    // 6. 创建 SplatData
    const splatData = new SplatData()
    splatData.elements = header.elements

    // 假设第一个 element 是 vertex
    const vertexElement = header.elements.find((e) => e.name === 'vertex')
    if (vertexElement) {
      splatData.numSplats = vertexElement.count
    }

    return splatData
  }

  /**
   * 解析 PLY 文件的 ArrayBuffer（高级 API，支持压缩格式）。
   *
   * 指定 keepCompressed=true 时可节省 3~7 倍内存；压缩格式部分预处理功能不可用。
   *
   * @param buffer - 文件原始字节
   * @param options - keepCompressed / propertyFilter / skipSH 组合
   * @returns 解压后的 SplatData 或压缩态的 CompressedSplatData
   */
  parseAdvanced(
    buffer: ArrayBuffer,
    options?: { keepCompressed?: boolean; propertyFilter?: string[]; skipSH?: boolean }
  ): SplatData | CompressedSplatData {
    // 1. 检测文件格式
    const format = this.detectFormat(buffer)

    // 2. 根据格式选择解析器
    if (format === 'splat') {
      return this.parseSplatFormat(buffer)
    }

    // 3. 解析 PLY header
    const header = this.parseHeader(buffer)

    // 4. 根据 PLY 格式类型选择解析方法
    if (header.isCompressed) {
      const compressed = this.parseCompressedBody(buffer, header)

      // 只有明确指定 keepCompressed: true 时才保留压缩格式
      if (options?.keepCompressed === true) {
        return compressed // 返回压缩格式
      } else {
        return this.decompressToSplatData(compressed) // 解压
      }
    }

    // 5. 解析标准 binary body
    this.parseBody(buffer, header.headerEnd, header.elements, {
      ...(options?.propertyFilter ? { propertyFilter: options.propertyFilter } : {}),
      ...(options?.skipSH !== undefined ? { skipSH: options.skipSH } : {}),
    })

    // 6. 创建 SplatData
    const splatData = new SplatData()
    splatData.elements = header.elements

    // 假设第一个 element 是 vertex
    const vertexElement = header.elements.find((e) => e.name === 'vertex')
    if (vertexElement) {
      splatData.numSplats = vertexElement.count
    }

    return splatData
  }

  /**
   * 检测文件格式
   * @param buffer 文件数据
   * @returns 格式类型: 'ply' | 'splat'
   */
  private detectFormat(buffer: ArrayBuffer): 'ply' | 'splat' {
    // .splat 格式没有明确的魔数，但可以通过以下特征判断：
    // 1. 文件大小必须是 32 的倍数（每个 splat 32 字节）
    // 2. 不以 "ply" 开头

    const decoder = new TextDecoder('utf-8')
    const headerBytes = new Uint8Array(buffer, 0, Math.min(buffer.byteLength, 10))
    const headerText = decoder.decode(headerBytes)

    // 检查是否为 PLY 格式
    if (headerText.toLowerCase().startsWith('ply')) {
      return 'ply'
    }

    // 检查是否为 .splat 格式
    if (buffer.byteLength % 32 === 0 && buffer.byteLength >= 32) {
      return 'splat'
    }

    // 默认尝试作为 PLY 解析
    return 'ply'
  }

  /**
   * 解析PLY header
   *
   * 查找 "end_header\n" 分隔符
   * 解析 element 和 property 定义
   * 检测是否为压缩格式（有 chunk element 和 packed_position 属性）
   */
  private parseHeader(buffer: ArrayBuffer): PlyHeader {
    // 读取前部分作为文本（假设header不超过64KB）
    const headerBytes = new Uint8Array(buffer, 0, Math.min(buffer.byteLength, 65536))
    const decoder = new TextDecoder('utf-8')
    const headerText = decoder.decode(headerBytes)

    // 查找 end_header
    const endHeaderPos = headerText.indexOf('end_header')
    if (endHeaderPos === -1) {
      throw new Error('Invalid PLY file: missing end_header')
    }

    // 计算实际的 header 结束位置（包含换行符）
    const endHeaderLine = headerText.indexOf('\n', endHeaderPos)
    if (endHeaderLine === -1) {
      throw new Error('Invalid PLY file: malformed end_header')
    }
    const headerEnd = endHeaderLine + 1

    // 分行解析
    const lines = headerText.substring(0, headerEnd).split('\n')
    const elements: PlyElement[] = []
    let currentElement: PlyElement | null = null
    let hasChunkElement = false
    let hasPackedPosition = false

    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed || trimmed === 'ply' || trimmed === 'end_header') {
        continue
      }

      // format binary_little_endian 1.0
      if (trimmed.startsWith('format')) {
        const parts = trimmed.split(/\s+/)
        if (parts[1] !== 'binary_little_endian') {
          throw new Error(`Unsupported PLY format: ${parts[1]}`)
        }
        continue
      }

      // element vertex 1000000
      if (trimmed.startsWith('element')) {
        const parts = trimmed.split(/\s+/)
        if (parts.length < 3) {
          throw new Error(`Invalid element line: ${trimmed}`)
        }
        currentElement = {
          name: parts[1],
          count: parseInt(parts[2], 10),
          properties: [],
        }
        elements.push(currentElement)

        // 检测 chunk element（压缩格式标志）
        if (parts[1] === 'chunk') {
          hasChunkElement = true
        }
        continue
      }

      // property float x
      if (trimmed.startsWith('property')) {
        if (!currentElement) {
          throw new Error('Property defined before element')
        }

        const parts = trimmed.split(/\s+/)
        if (parts.length < 3) {
          throw new Error(`Invalid property line: ${trimmed}`)
        }

        const propType = parts[1]
        const propName = parts[2]

        currentElement.properties.push({
          name: propName,
          type: propType,
          storage: null, // 稍后在 parseBody 中分配
        })

        // 检测 packed_position 属性（压缩格式标志）
        if (propName === 'packed_position') {
          hasPackedPosition = true
        }
      }

      // comment 等其他行忽略
    }

    // 判断是否为压缩格式
    const isCompressed = hasChunkElement && hasPackedPosition

    return {
      elements,
      headerEnd,
      isCompressed,
    }
  }

  /**
   * 解析PLY binary body
   *
   * 根据 header 定义的属性顺序逐个读取
   * 创建对应的 TypedArray 存储
   *
   * 优化策略：
   * 1. Float32 快速路径：当所有属性都是 float 时使用零拷贝
   * 2. 属性过滤：只加载需要的属性，跳过其他数据
   * 3. 批量处理：优化内存访问模式
   */
  private parseBody(
    buffer: ArrayBuffer,
    headerEnd: number,
    elements: PlyElement[],
    options?: SplatLoaderOptions
  ): void {
    // 尝试使用 Float32 快速路径
    if (this.tryFastPathFloat32(buffer, headerEnd, elements, options)) {
      return
    }

    // 常规路径
    const dataView = new DataView(buffer, headerEnd)
    let offset = 0

    for (const element of elements) {
      // 计算每个元素的字节大小
      let _elementByteSize = 0
      for (const prop of element.properties) {
        _elementByteSize += this.getTypeSize(prop.type)
      }

      // 构建属性过滤器（结合 propertyFilter 和 skipSH）
      const shouldIncludeProp = (propName: string): boolean => {
        // 如果设置了 skipSH，跳过所有 f_rest_* 属性
        if (options?.skipSH && propName.startsWith('f_rest_')) {
          return false
        }
        // 如果设置了 propertyFilter，只包含列表中的属性
        if (options?.propertyFilter) {
          return options.propertyFilter.includes(propName)
        }
        // 默认包含所有属性
        return true
      }

      // 为每个属性分配存储空间（只分配需要的）
      for (const prop of element.properties) {
        if (!shouldIncludeProp(prop.name)) {
          prop.storage = null // 标记为跳过
          continue
        }

        if (prop.type === 'float') {
          prop.storage = new Float32Array(element.count)
        } else if (prop.type === 'uchar') {
          prop.storage = new Uint8Array(element.count)
        } else if (prop.type === 'int') {
          prop.storage = new Int32Array(element.count)
        } else {
          throw new Error(`Unsupported property type: ${prop.type}`)
        }
      }

      // 批量读取数据
      for (let i = 0; i < element.count; i++) {
        for (const prop of element.properties) {
          const propSize = this.getTypeSize(prop.type)

          // 如果属性被过滤，直接跳过字节
          if (!prop.storage) {
            offset += propSize
            continue
          }

          // 读取值
          const value = this.readValue(dataView, offset, prop.type)

          if (prop.storage instanceof Float32Array) {
            prop.storage[i] = value
          } else if (prop.storage instanceof Uint8Array) {
            prop.storage[i] = value
          } else if (prop.storage instanceof Int32Array) {
            prop.storage[i] = value
          }

          offset += propSize
        }
      }
    }
  }

  /**
   * 尝试使用 Float32 快速路径
   *
   * 当满足以下条件时使用零拷贝优化：
   * 1. 所有属性都是 float 类型
   * 2. 属性按顺序排列，无间隙
   *
   * @returns true 表示使用了快速路径，false 表示需要使用常规路径
   */
  private tryFastPathFloat32(
    buffer: ArrayBuffer,
    headerEnd: number,
    elements: PlyElement[],
    options?: SplatLoaderOptions
  ): boolean {
    // 只处理单个 vertex element
    if (elements.length !== 1 || elements[0].name !== 'vertex') {
      return false
    }

    const element = elements[0]

    // 检查是否所有属性都是 float
    const allFloat = element.properties.every((p) => p.type === 'float')
    if (!allFloat) {
      return false
    }

    // 如果有属性过滤，不使用快速路径（需要跳过某些属性）
    if (options?.propertyFilter || options?.skipSH) {
      return false
    }

    // 计算每个顶点的字节大小和属性数量
    const propsPerVertex = element.properties.length
    const vertexByteSize = propsPerVertex * 4 // float32 = 4 bytes
    const totalVertices = element.count

    // 检查数据大小是否匹配
    const expectedSize = totalVertices * vertexByteSize
    const actualSize = buffer.byteLength - headerEnd
    if (expectedSize !== actualSize) {
      return false
    }

    // 检查对齐（Float32Array 需要 4 字节对齐）
    if (headerEnd % 4 !== 0) {
      return false
    }

    // 使用快速路径：创建 Float32Array 视图
    const allData = new Float32Array(buffer, headerEnd, totalVertices * propsPerVertex)

    // 为每个属性创建存储（交错数据需要复制）
    for (let propIdx = 0; propIdx < element.properties.length; propIdx++) {
      const prop = element.properties[propIdx]
      prop.storage = new Float32Array(totalVertices)

      // 从交错数据中提取该属性
      for (let vertexIdx = 0; vertexIdx < totalVertices; vertexIdx++) {
        prop.storage[vertexIdx] = allData[vertexIdx * propsPerVertex + propIdx]
      }
    }

    return true
  }

  /**
   * 获取PLY类型的字节大小
   */
  private getTypeSize(type: string): number {
    switch (type) {
      case 'float':
        return 4
      case 'uchar':
        return 1
      case 'int':
        return 4
      case 'double':
        return 8
      case 'short':
        return 2
      case 'ushort':
        return 2
      default:
        throw new Error(`Unknown type: ${type}`)
    }
  }

  /**
   * 从DataView读取指定类型的值
   */
  private readValue(dataView: DataView, offset: number, type: string): number {
    switch (type) {
      case 'float':
        return dataView.getFloat32(offset, true) // little endian
      case 'uchar':
        return dataView.getUint8(offset)
      case 'int':
        return dataView.getInt32(offset, true)
      case 'double':
        return dataView.getFloat64(offset, true)
      case 'short':
        return dataView.getInt16(offset, true)
      case 'ushort':
        return dataView.getUint16(offset, true)
      default:
        throw new Error(`Unknown type: ${type}`)
    }
  }

  /**
   * 带进度报告的 fetch
   */
  private async fetchWithProgress(url: string, options?: SplatLoaderOptions): Promise<ArrayBuffer> {
    const response = await fetch(url, options?.signal ? { signal: options.signal } : undefined)

    if (!response.ok) {
      throw new SplatError(
        SplatErrorCode.INVALID_BINARY_DATA,
        `加载失败: ${response.status} ${response.statusText}`
      )
    }

    const contentLength = response.headers.get('Content-Length')
    if (!contentLength || !response.body) {
      // 如果没有 Content-Length 或 body，直接返回
      return response.arrayBuffer()
    }

    const total = parseInt(contentLength, 10)
    let loaded = 0
    const chunks: Uint8Array[] = []
    const reader = response.body.getReader()

    try {
      while (true) {
        // 检查中止信号
        if (options?.signal?.aborted) {
          reader.cancel()
          throw new DOMException('Aborted', 'AbortError')
        }

        const { done, value } = await reader.read()
        if (done) break
        chunks.push(value)
        loaded += value.length
        options?.onProgress?.((loaded / total) * 0.3, '加载文件')
      }
    } catch (error) {
      reader.cancel()
      throw error
    }

    // 合并 chunks
    const buffer = new Uint8Array(loaded)
    let offset = 0
    for (const chunk of chunks) {
      buffer.set(chunk, offset)
      offset += chunk.length
    }

    return buffer.buffer
  }

  /**
   * 预处理 SplatData
   */
  private preprocess(data: SplatData, options?: SplatLoaderOptions): void {
    // 坐标系转换
    if (options?.coordinateSystem || options?.targetCoordinateSystem) {
      const from = options.coordinateSystem ?? CoordinateSystem.detect(data)
      const to = options.targetCoordinateSystem ?? CoordinateSystemType.Y_UP
      if (from !== to) {
        CoordinateSystem.convert(data, from, to)
      }
    }

    // 归一化四元数
    if (options?.normalizeQuaternions !== false) {
      this.normalizeQuaternions(data)
    }

    // 修正负缩放
    if (options?.fixNegativeScales !== false) {
      this.fixNegativeScales(data)
    }

    // 过滤低alpha（标记为完全透明而非删除）
    if (options?.alphaRemovalThreshold !== undefined && options.alphaRemovalThreshold > 0) {
      this.markLowAlpha(data, options.alphaRemovalThreshold)
    }
  }

  /**
   * 归一化四元数
   */
  private normalizeQuaternions(data: SplatData): void {
    const rot0 = data.getProp('rot_0') as Float32Array
    const rot1 = data.getProp('rot_1') as Float32Array
    const rot2 = data.getProp('rot_2') as Float32Array
    const rot3 = data.getProp('rot_3') as Float32Array

    if (!rot0 || !rot1 || !rot2 || !rot3) return

    for (let i = 0; i < data.numSplats; i++) {
      const len = Math.sqrt(rot0[i] ** 2 + rot1[i] ** 2 + rot2[i] ** 2 + rot3[i] ** 2)
      if (len > 0 && Math.abs(len - 1.0) > 0.001) {
        rot0[i] /= len
        rot1[i] /= len
        rot2[i] /= len
        rot3[i] /= len
      }
    }
  }

  /**
   * 修正负缩放
   * 注意：scale在PLY中是log空间存储，负值表示非常小的缩放
   * exp(negative)是有效的小正数，所以这里实际不需要特殊处理
   */
  private fixNegativeScales(data: SplatData): void {
    const scale0 = data.getProp('scale_0') as Float32Array
    const scale1 = data.getProp('scale_1') as Float32Array
    const scale2 = data.getProp('scale_2') as Float32Array

    if (!scale0 || !scale1 || !scale2) return

    // 在log空间，负值是正常的（表示小的缩放）
    // 这里预留接口，未来如果需要可以添加处理逻辑
  }

  /**
   * 标记低透明度splat
   */
  private markLowAlpha(data: SplatData, threshold: number): void {
    const opacity = data.getProp('opacity') as Float32Array
    if (!opacity) return

    // sigmoid(x) < threshold 对应 x < ln(threshold/(1-threshold))
    const logitThreshold = Math.log(threshold / (1 - threshold))

    for (let i = 0; i < data.numSplats; i++) {
      if (opacity[i] < logitThreshold) {
        // 标记为完全透明
        opacity[i] = -100 // sigmoid(-100) ≈ 0
      }
    }
  }

  // ============================================================
  // 压缩 PLY 格式解析
  // ============================================================

  /**
   * 解析压缩PLY格式的body
   *
   * 压缩格式结构:
   * - chunk element: 每256个splat一个chunk，包含位置/缩放/颜色的min-max范围
   * - vertex element: 4个uint32打包的数据（位置/旋转/缩放/颜色）
   * - 可选的SH数据
   *
   * @param buffer 原始buffer
   * @param header 解析后的header
   * @returns 压缩格式的 CompressedSplatData
   */
  private parseCompressedBody(buffer: ArrayBuffer, header: PlyHeader): CompressedSplatData {
    const chunkElement = header.elements.find((e) => e.name === 'chunk')
    const vertexElement = header.elements.find((e) => e.name === 'vertex')

    if (!chunkElement || !vertexElement) {
      throw new Error('Invalid compressed PLY: missing chunk or vertex element')
    }

    const numChunks = chunkElement.count
    const numSplats = vertexElement.count

    // 计算chunk大小（12或18个float）
    const chunkProps = chunkElement.properties.length
    const chunkSize = chunkProps // 每个属性一个float

    // 检测是否有颜色范围（18个float vs 12个float）
    const hasColorRange = chunkSize > 12

    // 检测SH数据
    const shElement = header.elements.find((e) =>
      e.properties.some((p) => p.name.startsWith('f_rest_'))
    )
    const shBands = this.detectSHBandsFromElement(shElement)

    // 读取二进制数据
    const dataView = new DataView(buffer, header.headerEnd)
    let offset = 0

    // 1. 读取chunk数据
    const chunkData = new Float32Array(numChunks * chunkSize)
    for (let c = 0; c < numChunks; c++) {
      for (let p = 0; p < chunkSize; p++) {
        chunkData[c * chunkSize + p] = dataView.getFloat32(offset, true)
        offset += 4
      }
    }

    // 2. 读取vertex数据（每个splat 4个uint32）
    const vertexData = new Uint32Array(numSplats * 4)
    for (let i = 0; i < numSplats * 4; i++) {
      vertexData[i] = dataView.getUint32(offset, true)
      offset += 4
    }

    // 3. 读取SH数据（可选）
    let shData0: Uint8Array | null = null
    let shData1: Uint8Array | null = null
    let shData2: Uint8Array | null = null

    if (shBands > 0) {
      const shDataSize = numSplats * 16 // 每个通道16字节

      shData0 = new Uint8Array(shDataSize)
      shData1 = new Uint8Array(shDataSize)
      shData2 = new Uint8Array(shDataSize)

      for (let i = 0; i < shDataSize; i++) {
        shData0[i] = dataView.getUint8(offset++)
      }
      for (let i = 0; i < shDataSize; i++) {
        shData1[i] = dataView.getUint8(offset++)
      }
      for (let i = 0; i < shDataSize; i++) {
        shData2[i] = dataView.getUint8(offset++)
      }
    }

    // 4. 返回 CompressedSplatData
    const compressed = new CompressedSplatData({
      numSplats,
      numChunks,
      chunkSize,
      chunkData,
      vertexData,
      hasColorRange,
      shBands,
      shData0,
      shData1,
      shData2,
    })

    return compressed
  }

  /**
   * 从element检测SH bands数量
   */
  private detectSHBandsFromElement(element?: PlyElement): number {
    if (!element) return 0

    let maxRestIndex = -1
    for (const prop of element.properties) {
      if (prop.name.startsWith('f_rest_')) {
        const idx = parseInt(prop.name.substring(7), 10)
        if (!Number.isNaN(idx) && idx > maxRestIndex) {
          maxRestIndex = idx
        }
      }
    }

    if (maxRestIndex < 0) return 0
    if (maxRestIndex < 9) return 1 // 3 coeffs * 3 channels = 9
    if (maxRestIndex < 24) return 2 // 8 coeffs * 3 channels = 24
    return 3 // 15 coeffs * 3 channels = 45
  }

  /**
   * 解压压缩数据到标准SplatData格式
   */
  private decompressToSplatData(compressed: CompressedSplatData): SplatData {
    const numSplats = compressed.numSplats
    const shBands = compressed.shBands

    // 分配存储空间
    const x = new Float32Array(numSplats)
    const y = new Float32Array(numSplats)
    const z = new Float32Array(numSplats)
    const rot_0 = new Float32Array(numSplats)
    const rot_1 = new Float32Array(numSplats)
    const rot_2 = new Float32Array(numSplats)
    const rot_3 = new Float32Array(numSplats)
    const scale_0 = new Float32Array(numSplats)
    const scale_1 = new Float32Array(numSplats)
    const scale_2 = new Float32Array(numSplats)
    const f_dc_0 = new Float32Array(numSplats)
    const f_dc_1 = new Float32Array(numSplats)
    const f_dc_2 = new Float32Array(numSplats)
    const opacity = new Float32Array(numSplats)

    // SH数据
    const shCoeffsPerChannel = shBands > 0 ? [3, 8, 15][shBands - 1] : 0
    const fRest: Float32Array[] = []
    for (let i = 0; i < shCoeffsPerChannel * 3; i++) {
      fRest.push(new Float32Array(numSplats))
    }

    // 使用迭代器批量解压
    const iter = compressed.createIterator()
    for (let i = 0; i < numSplats; i++) {
      const splat = iter.read(i)

      x[i] = splat.position[0]
      y[i] = splat.position[1]
      z[i] = splat.position[2]

      rot_0[i] = splat.rotation[0] // w
      rot_1[i] = splat.rotation[1] // x
      rot_2[i] = splat.rotation[2] // y
      rot_3[i] = splat.rotation[3] // z

      scale_0[i] = splat.scale[0]
      scale_1[i] = splat.scale[1]
      scale_2[i] = splat.scale[2]

      f_dc_0[i] = splat.color[0]
      f_dc_1[i] = splat.color[1]
      f_dc_2[i] = splat.color[2]

      opacity[i] = splat.opacity

      // SH数据
      if (splat.sh) {
        for (let k = 0; k < fRest.length; k++) {
          fRest[k][i] = splat.sh[k]
        }
      }
    }

    // 构建SplatData
    const splatData = new SplatData()
    splatData.numSplats = numSplats

    // 构建属性列表
    const properties: PlyProperty[] = [
      { name: 'x', type: 'float', storage: x },
      { name: 'y', type: 'float', storage: y },
      { name: 'z', type: 'float', storage: z },
      { name: 'f_dc_0', type: 'float', storage: f_dc_0 },
      { name: 'f_dc_1', type: 'float', storage: f_dc_1 },
      { name: 'f_dc_2', type: 'float', storage: f_dc_2 },
      { name: 'opacity', type: 'float', storage: opacity },
      { name: 'scale_0', type: 'float', storage: scale_0 },
      { name: 'scale_1', type: 'float', storage: scale_1 },
      { name: 'scale_2', type: 'float', storage: scale_2 },
      { name: 'rot_0', type: 'float', storage: rot_0 },
      { name: 'rot_1', type: 'float', storage: rot_1 },
      { name: 'rot_2', type: 'float', storage: rot_2 },
      { name: 'rot_3', type: 'float', storage: rot_3 },
    ]

    // 添加SH属性
    for (let i = 0; i < fRest.length; i++) {
      properties.push({
        name: `f_rest_${i}`,
        type: 'float',
        storage: fRest[i],
      })
    }

    splatData.elements = [
      {
        name: 'vertex',
        count: numSplats,
        properties,
      },
    ]

    return splatData
  }

  // ============================================================
  // 流式解析核心方法
  // ============================================================

  /**
   * 流式读取PLY文件
   *
   * 参考 PlayCanvas readPly 实现
   */
  private async readPlyStreaming(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    options?: SplatLoaderOptions,
    progressFunc?: (bytes: number) => void
  ): Promise<SplatData> {
    const magicBytes = new Uint8Array([112, 108, 121, 10]) // ply\n
    const endHeaderBytes = new Uint8Array([10, 101, 110, 100, 95, 104, 101, 97, 100, 101, 114, 10]) // \nend_header\n

    const streamBuf = new StreamBuf(reader, progressFunc)
    let headerLength: number

    // 1. 读取 header
    while (true) {
      await streamBuf.read()

      // 检查魔数
      const buffer = streamBuf.buffer
      if (buffer && streamBuf.remaining >= magicBytes.length) {
        if (!this.startsWith(buffer, magicBytes)) {
          throw new Error('Invalid PLY header')
        }
      }

      // 查找 end_header
      if (buffer) {
        const idx = this.findInBuffer(buffer, endHeaderBytes)
        if (idx !== -1) {
          headerLength = idx
          break
        }
      }
    }

    // 2. 解析 header
    const buffer = streamBuf.buffer!
    const headerText = new TextDecoder('ascii').decode(buffer.subarray(0, headerLength))
    const lines = headerText.split('\n')
    const header = this.parseHeaderLines(lines)

    // 3. 跳过 header，紧凑化缓冲区
    // streamBuf 的 head 需要指向 binary body 起始位置
    // 这里需要手动移动 head（由于 StreamBuf 没有公开方法，需要修改）
    // 暂时使用简单方法：记录 headerEnd
    const headerEnd = headerLength + endHeaderBytes.length

    // 4. 分配属性存储
    this.allocateStorage(header.elements, options)

    // 5. 流式读取 body
    if (this.isFloatPly(header.elements)) {
      await this.readFloatPlyStreaming(streamBuf, headerEnd, header.elements)
    } else {
      await this.readGeneralPlyStreaming(streamBuf, headerEnd, header.elements)
    }

    // 6. 构建 SplatData
    const splatData = new SplatData()
    splatData.elements = header.elements

    const vertexElement = header.elements.find((e) => e.name === 'vertex')
    if (vertexElement) {
      splatData.numSplats = vertexElement.count
    }

    return splatData
  }

  /**
   * Float32 快速路径流式解析
   */
  private async readFloatPlyStreaming(
    streamBuf: StreamBuf,
    headerEnd: number,
    elements: PlyElement[]
  ): Promise<void> {
    // 跳过 header
    // 注意：StreamBuf 可能已经读取了部分数据，需要计算偏移
    let offset = headerEnd

    const element = elements[0]
    const properties = element.properties
    const numProperties = properties.length
    const storage = properties.map((p) => p.storage as Float32Array)
    const inputSize = numProperties * 4 // 所有属性都是 float32

    let vertexIdx = 0

    // 创建 Float32Array 视图（需要根据当前 buffer 重建）
    let floatData: Float32Array | null = null
    const checkFloatData = () => {
      const buf = streamBuf.buffer!
      if (!floatData || floatData.buffer !== buf.buffer) {
        floatData = new Float32Array(buf.buffer, 0, buf.buffer.byteLength / 4)
      }
    }

    checkFloatData()

    while (vertexIdx < element.count) {
      // 等待足够的数据
      while (streamBuf.remaining - (offset - headerEnd) < inputSize) {
        await streamBuf.read()
        checkFloatData()
      }

      const available = Math.floor((streamBuf.remaining - (offset - headerEnd)) / inputSize)
      const toRead = Math.min(element.count - vertexIdx, available)

      // 解交错数据
      const floatOffset = offset / 4
      for (let j = 0; j < numProperties; ++j) {
        const s = storage[j]
        for (let n = 0; n < toRead; ++n) {
          s[n + vertexIdx] = floatData![floatOffset + n * numProperties + j]
        }
      }

      vertexIdx += toRead
      offset += toRead * inputSize
    }
  }

  /**
   * 通用流式解析
   */
  private async readGeneralPlyStreaming(
    streamBuf: StreamBuf,
    headerEnd: number,
    elements: PlyElement[]
  ): Promise<void> {
    // 跳过 header（调整 StreamBuf 的内部指针）
    // 由于 StreamBuf 没有公开 head 设置方法，这里需要手动跳过
    let bytesToSkip = headerEnd

    // 消耗掉 header 部分的数据
    while (bytesToSkip > 0) {
      const available = Math.min(bytesToSkip, streamBuf.remaining)
      if (available === 0) {
        await streamBuf.read()
        continue
      }
      // 通过读取 uint8 来移动 head 指针
      for (let i = 0; i < available; i++) {
        streamBuf.getUint8()
      }
      bytesToSkip -= available
    }

    // 紧凑化缓冲区
    streamBuf.compact()

    // 读取每个 element
    for (const element of elements) {
      // 计算每个元素的字节大小
      const inputSize = element.properties.reduce((a, p) => a + this.getTypeSize(p.type), 0)

      // 构建解析函数
      const propertyParsingFunctions = element.properties.map((p) => {
        if (p.storage) {
          switch (p.type) {
            case 'char':
              return (sb: StreamBuf, c: number) => {
                ;(p.storage as unknown as Int8Array)[c] = sb.getInt8()
              }
            case 'uchar':
              return (sb: StreamBuf, c: number) => {
                ;(p.storage as unknown as Uint8Array)[c] = sb.getUint8()
              }
            case 'short':
              return (sb: StreamBuf, c: number) => {
                ;(p.storage as unknown as Int16Array)[c] = sb.getInt16()
              }
            case 'ushort':
              return (sb: StreamBuf, c: number) => {
                ;(p.storage as unknown as Uint16Array)[c] = sb.getUint16()
              }
            case 'int':
              return (sb: StreamBuf, c: number) => {
                ;(p.storage as Int32Array)[c] = sb.getInt32()
              }
            case 'uint':
              return (sb: StreamBuf, c: number) => {
                ;(p.storage as unknown as Uint32Array)[c] = sb.getUint32()
              }
            case 'float':
              return (sb: StreamBuf, c: number) => {
                ;(p.storage as Float32Array)[c] = sb.getFloat32()
              }
            case 'double':
              return (sb: StreamBuf, c: number) => {
                ;(p.storage as unknown as Float64Array)[c] = sb.getFloat64()
              }
            default:
              throw new Error(`Unsupported property data type '${p.type}' in ply header`)
          }
        } else {
          // 跳过不需要的属性
          return (sb: StreamBuf, _c: number) => {
            const size = this.getTypeSize(p.type)
            for (let i = 0; i < size; i++) {
              sb.getUint8()
            }
          }
        }
      })

      let c = 0
      while (c < element.count) {
        // 等待足够的数据
        while (streamBuf.remaining < inputSize) {
          await streamBuf.read()
        }

        const toRead = Math.min(element.count - c, Math.floor(streamBuf.remaining / inputSize))

        for (let n = 0; n < toRead; ++n) {
          for (let j = 0; j < element.properties.length; ++j) {
            propertyParsingFunctions[j](streamBuf, c)
          }
          c++
        }
      }
    }
  }

  /**
   * 检查是否为纯 Float32 PLY
   */
  private isFloatPly(elements: PlyElement[]): boolean {
    return (
      elements.length === 1 &&
      elements[0].name === 'vertex' &&
      elements[0].properties.every((p) => p.type === 'float')
    )
  }

  /**
   * 分配属性存储空间
   */
  private allocateStorage(elements: PlyElement[], options?: SplatLoaderOptions): void {
    type TypedArrayConstructor =
      | Int8ArrayConstructor
      | Uint8ArrayConstructor
      | Int16ArrayConstructor
      | Uint16ArrayConstructor
      | Int32ArrayConstructor
      | Uint32ArrayConstructor
      | Float32ArrayConstructor
      | Float64ArrayConstructor

    const dataTypeMap = new Map<string, TypedArrayConstructor>([
      ['char', Int8Array],
      ['uchar', Uint8Array],
      ['short', Int16Array],
      ['ushort', Uint16Array],
      ['int', Int32Array],
      ['uint', Uint32Array],
      ['float', Float32Array],
      ['double', Float64Array],
    ])

    const shouldIncludeProp = (propName: string): boolean => {
      if (options?.skipSH && propName.startsWith('f_rest_')) {
        return false
      }
      if (options?.propertyFilter) {
        return options.propertyFilter.includes(propName)
      }
      return true
    }

    elements.forEach((e) => {
      e.properties.forEach((p) => {
        const storageType = dataTypeMap.get(p.type)
        if (storageType) {
          const storage = shouldIncludeProp(p.name) ? new storageType(e.count) : null
          // 使用类型断言，因为 PlyProperty.storage 只定义了部分 TypedArray 类型
          p.storage = storage as Float32Array | Uint8Array | Int32Array | null
        }
      })
    })
  }

  /**
   * 解析 header 行（提取 elements 和 format）
   */
  private parseHeaderLines(lines: string[]): { elements: PlyElement[]; format: string } {
    const elements: PlyElement[] = []
    let format = 'binary_little_endian'
    let currentElement: PlyElement | null = null

    for (let i = 1; i < lines.length; ++i) {
      const words = lines[i].split(' ')

      switch (words[0]) {
        case 'comment':
          break
        case 'format':
          format = words[1]
          break
        case 'element':
          currentElement = {
            name: words[1],
            count: parseInt(words[2], 10),
            properties: [],
          }
          elements.push(currentElement)
          break
        case 'property': {
          if (!currentElement) {
            throw new Error('Property defined before element')
          }
          currentElement.properties.push({
            type: words[1],
            name: words[2],
            storage: null,
          })
          break
        }
        default:
          // 忽略未知行
          break
      }
    }

    if (format !== 'binary_little_endian') {
      throw new Error('Unsupported ply format')
    }

    return { elements, format }
  }

  /**
   * 在 buffer 中查找序列
   */
  private findInBuffer(buf: Uint8Array, search: Uint8Array): number {
    const endIndex = buf.length - search.length
    for (let i = 0; i <= endIndex; ++i) {
      let j = 0
      for (; j < search.length; ++j) {
        if (buf[i + j] !== search[j]) {
          break
        }
      }
      if (j === search.length) {
        return i
      }
    }
    return -1
  }

  /**
   * 检查 buffer 是否以指定序列开头
   */
  private startsWith(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length < b.length) {
      return false
    }
    for (let i = 0; i < b.length; ++i) {
      if (a[i] !== b[i]) {
        return false
      }
    }
    return true
  }

  // ============================================================
  // .splat 二进制格式解析
  // ============================================================

  /**
   * 解析 .splat 二进制格式
   *
   * .splat 格式规范（每个 splat 32 字节）：
   * - 位置（x,y,z）: 3 x float32 (12 字节) - 世界坐标
   * - 缩放（sx,sy,sz）: 3 x float32 (12 字节) - 已经是 exp 后的实际缩放值
   * - 颜色（r,g,b,a）: 4 x uint8 (4 字节) - RGBA, 范围 0-255
   * - 旋转（w,x,y,z）: 4 x uint8 (4 字节) - 四元数，范围 -128 到 127（归一化）
   *
   * @param buffer 原始二进制数据
   * @returns 解析后的 SplatData
   */
  private parseSplatFormat(buffer: ArrayBuffer): SplatData {
    return parseSplat(buffer)
  }
}
